import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode, type OfType } from '@reconcept-platform/shared/data-access';
import {
  type AddSecondaryWorkspaceToDashboardMutation,
  type AddSecondaryWorkspaceToDashboardMutationVariables,
  type CreateDashboardInput,
  type CreateDashboardMutation,
  type CreateDashboardMutationVariables,
  type DashboardMatrixQueryQuery,
  type DashboardMatrixQueryQueryVariables,
  type DashboardQualityCalendarQuery,
  type DashboardQualityCalendarQueryVariables,
  type DashboardScopeForRoleModalQueryQuery,
  type DashboardScopeForRoleModalQueryQueryVariables,
  type DashboardsQueryQuery,
  type DashboardsQueryQueryVariables,
  type DashboardsWithViewerCanMinimalQuery,
  type DashboardsWithViewerCanMinimalQueryVariables,
  type DashboardWithRolesQueryQuery,
  type DashboardWithRolesQueryQueryVariables,
  type DashboardWithWorkspacesAndCardCountQueryQuery,
  type DashboardWithWorkspacesAndCardCountQueryQueryVariables,
  type DashboardWithWorkspacesQueryQuery,
  type DashboardWithWorkspacesQueryQueryVariables,
  type DestroyDashboardMutation,
  type DestroyDashboardMutationVariables,
  type RemoveSecondaryWorkspaceFromDashboardMutation,
  type RemoveSecondaryWorkspaceFromDashboardMutationVariables,
  type UpdateDashboardInput,
  type UpdateDashboardMutation,
  type UpdateDashboardMutationVariables,
  type WorkspaceFilter,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { dashboardFormatWithDashboardsAndWorkspacesQuery } from '../dashboard-format/dashboard-format.queries';
import { eventCardItemFragment } from '../event-card/event-card.fragments';
import { qualityInstrumentItemFragment } from '../quality-instrument/quality-instrument.fragments';
import { workspaceItemFragment, workspaceViewerCanFragment } from '../workspace/workspace.fragments';
import { workspaceWithSecondaryDashboardsQuery } from '../workspace/workspace.queries';
import { dashboardItemFragment } from './dashboard.fragments';
import {
  dashboardMatrixQuery,
  dashboardScopeForRoleModalQuery,
  dashboardsQuery,
  dashboardsWithViewerCanMinimalQuery,
  dashboardWithDashboardFormatQuery,
  dashboardWithRolesQuery,
  dashboardWithWorkspacesAndCardCountQuery,
  DashboardWithWorkspacesQuery,
} from './dashboard.queries';

export type DashboardWithWorkspacesAndCardCountQuery_DashBoard = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  DashboardWithWorkspacesAndCardCountQueryQuery['node'],
  'Dashboard'
>;

export type DashboardWithWorkspacesQuery_Dashboard = OfType<
  NonNullable<DashboardWithWorkspacesQueryQuery['node']>,
  'Dashboard'
>;
export type DashboardwithWorkspacesQuery_Workspace =
  DashboardWithWorkspacesQuery_Dashboard['workspaces']['nodes'][number];

// @ts-expect-error TS2339 property does not exist
export type DashboardsQuery_Dashboard = DashboardsQueryQuery['dashboards']['edges'][0]['node'];

// @ts-expect-error TS2344 type constraint not satisfied
export type DashboardWithRolesQuery_Dashboard = OfType<DashboardWithRolesQueryQuery['node'], 'Dashboard'>;

// @ts-expect-error TS2344 type constraint not satisfied
export type DashboardScopeForRoleModal_Dashboard = OfType<DashboardScopeForRoleModalQueryQuery['node'], 'Dashboard'>;
export type DashboardScopeForRoleModal_Workspace = DashboardScopeForRoleModal_Dashboard['workspaces']['nodes'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type DashboardMatrixQuery_Dashboard = OfType<DashboardMatrixQueryQuery['node'], 'Dashboard'>;
export type DashboardMatrixQuery_SubDomainToWorkspaceConnectionEdge =
  DashboardMatrixQuery_Dashboard['parentDomains']['nodes'][number]['subDomains']['nodes'][number]['secondaryWorkspaces']['edges'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type DashboardQualityCalendarQuery_Dashboard = OfType<DashboardQualityCalendarQuery['node'], 'Dashboard'>;
export type DashboardQualityCalendarQuery_Workspace =
  DashboardQualityCalendarQuery_Dashboard['workspaces']['nodes'][number];
@Injectable({
  providedIn: 'root',
})
export class DashboardApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  updateDashboard(updateDashboardInput: UpdateDashboardInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateDashboardMutation, UpdateDashboardMutationVariables>({
          mutation: gql`
            mutation UpdateDashboard($input: UpdateDashboardInput!) {
              updateDashboard(input: $input) {
                dashboard {
                  id
                  title
                  description
                  useAlternativeTitle
                  featuredWorkspaceId
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: updateDashboardInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateDashboard.dashboard))
    );
  }

  createDashboard(input: CreateDashboardInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<CreateDashboardMutation, CreateDashboardMutationVariables>({
          mutation: gql`
            mutation CreateDashboard($input: CreateDashboardInput!) {
              createDashboard(input: $input) {
                dashboard {
                  id
                  title
                  description
                  useAlternativeTitle
                  featuredWorkspaceId

                  dashboardFormat {
                    id
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: dashboardFormatWithDashboardsAndWorkspacesQuery,
              variables: {
                id: input.dashboardFormatId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createDashboard.dashboard))
    );
  }

  destroyDashboard(dashboardId: string, dashboardFormatId: string) {
    return this.workspaceGraphQLService.mutateV2<DestroyDashboardMutation, DestroyDashboardMutationVariables>({
      mutation: gql`
        mutation DestroyDashboard($input: DestroyDashboardInput!) {
          destroyDashboard(input: $input) {
            errors {
              path
              message
            }
          }
        }
      `,
      variables: {
        input: {
          dashboardId,
        },
      },
      refetchQueries: [
        {
          query: dashboardFormatWithDashboardsAndWorkspacesQuery,
          variables: {
            id: dashboardFormatId,
          },
        },
      ],
    });
  }

  addSecondaryWorkspaceToDashboard(dashboardId: string, workspaceId: string): Observable<unknown> {
    return (
      this.workspaceGraphQLService
        .mutateV2<AddSecondaryWorkspaceToDashboardMutation, AddSecondaryWorkspaceToDashboardMutationVariables>({
          mutation: gql`
            mutation AddSecondaryWorkspaceToDashboard($input: AddWorkspaceToSecondaryDashboardInput!) {
              addWorkspaceToSecondaryDashboard(input: $input) {
                dashboard {
                  id
                  title
                  secondaryWorkspaces {
                    nodes {
                      id
                      title
                    }
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: {
              dashboardId,
              workspaceId,
            },
          },
          refetchQueries: [
            {
              query: workspaceWithSecondaryDashboardsQuery,
              variables: {
                id: workspaceId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.addWorkspaceToSecondaryDashboard.dashboard))
    );
  }

  removeSecondaryWorkspaceFromDashboard(dashboardId: string, secondaryWorkspaceId: string): Observable<unknown> {
    return (
      this.workspaceGraphQLService
        .mutateV2<
          RemoveSecondaryWorkspaceFromDashboardMutation,
          RemoveSecondaryWorkspaceFromDashboardMutationVariables
        >({
          mutation: gql`
            mutation RemoveSecondaryWorkspaceFromDashboard($input: RemoveWorkspaceFromSecondaryDashboardInput!) {
              removeWorkspaceFromSecondaryDashboard(input: $input) {
                dashboard {
                  id
                  title
                  secondaryWorkspaces {
                    nodes {
                      id
                      title
                    }
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: {
              dashboardId,
              workspaceId: secondaryWorkspaceId,
            },
          },
          refetchQueries: [
            {
              query: workspaceWithSecondaryDashboardsQuery,
              variables: {
                id: secondaryWorkspaceId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.removeWorkspaceFromSecondaryDashboard.dashboard))
    );
  }

  watchDashboardQualityCalendar(dashboardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<DashboardQualityCalendarQuery, DashboardQualityCalendarQueryVariables>({
        query: gql`
          query DashboardQualityCalendar($id: ID!) {
            node(id: $id) {
              ... on Dashboard {
                ...DashboardItemFragment
                featuredWorkspaceId

                qualityInstruments {
                  nodes {
                    ...QualityInstrumentItem
                  }
                }
                workspaces {
                  nodes {
                    ...WorkspaceItemFragment

                    ...WorkspaceViewerCanFragment

                    accreditationEndDate
                    qualityInstruments {
                      edges {
                        mostRecentEventCard {
                          ...EventCardItemFragment
                        }
                        firstUpcomingEventCard {
                          ...EventCardItemFragment
                        }
                        node {
                          ...QualityInstrumentItem
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ${dashboardItemFragment}
          ${workspaceItemFragment}
          ${workspaceViewerCanFragment}
          ${eventCardItemFragment}
          ${qualityInstrumentItemFragment}
        `,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }

  watchDashboardWithRoles(dashboardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<DashboardWithRolesQueryQuery, DashboardWithRolesQueryQueryVariables>({
        query: dashboardWithRolesQuery,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }

  watchDashboardScopeForRoleModal(dashboardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<DashboardScopeForRoleModalQueryQuery, DashboardScopeForRoleModalQueryQueryVariables>({
        query: dashboardScopeForRoleModalQuery,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }

  watchAllDashboardsWithViewerCanMinimal() {
    return (
      this.workspaceGraphQLService
        .watchQuery<DashboardsWithViewerCanMinimalQuery, DashboardsWithViewerCanMinimalQueryVariables>({
          query: dashboardsWithViewerCanMinimalQuery,
        })
        // @ts-expect-error TS18047 possibly null | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.dashboards.edges.map((dashboard) => dashboard.node)))
    );
  }

  watchAllDashboardsWithWorkspaces() {
    return (
      this.workspaceGraphQLService
        .watchQuery<DashboardsQueryQuery, DashboardsQueryQueryVariables>({
          query: dashboardsQuery,
        })
        // @ts-expect-error TS18047 possibly null | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.dashboards.edges.map((dashboard) => dashboard.node)))
    );
  }

  watchDashboardWithDashboardFormatQuery(dashboardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<DashboardWithWorkspacesQueryQuery, DashboardWithWorkspacesQueryQueryVariables>({
        query: dashboardWithDashboardFormatQuery,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }

  watchDashboardWithWorkspaces(dashboardId: string): Observable<DashboardWithWorkspacesQuery_Dashboard> {
    return this.workspaceGraphQLService
      .watchQuery<DashboardWithWorkspacesQueryQuery, DashboardWithWorkspacesQueryQueryVariables>({
        query: DashboardWithWorkspacesQuery,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }

  watchDashboardWithWorkspacesAndCardCount(dashboardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<
        DashboardWithWorkspacesAndCardCountQueryQuery,
        DashboardWithWorkspacesAndCardCountQueryQueryVariables
      >({
        query: dashboardWithWorkspacesAndCardCountQuery,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }

  watchDashboardMatrix(
    dashboardId: string,
    workspaceFilterForWorkspaces: WorkspaceFilter,
    workspaceFilterForSecondaryWorkspaces: WorkspaceFilter,
  ) {
    return this.workspaceGraphQLService
      .watchQuery<DashboardMatrixQueryQuery, DashboardMatrixQueryQueryVariables>({
        query: dashboardMatrixQuery,
        variables: {
          id: dashboardId,
          filterForWorkspaces: workspaceFilterForWorkspaces,
          filterForSecondaryWorkspaces: workspaceFilterForSecondaryWorkspaces,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Dashboard')));
  }
}
